import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Search, 
  Plus, 
  Edit,
  Trash2,
  Truck,
  MapPin,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Settings,
  Power,
  PowerOff
} from 'lucide-react';
import api from '@/services/api';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';

export default function GestionMateriel() {
  const navigate = useNavigate();
  const { categoriesCACES: ctxCategoriesCACES } = useApp();
  const [categoriesCACES, setCategoriesCACES] = useState([]);
  const [materiel, setMateriel] = useState([]);
  const [typesMateriel, setTypesMateriel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [zoneFilter, setZoneFilter] = useState('all');
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMateriel, setSelectedMateriel] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    designation: '',
    type_materiel_id: '',
    categorie_caces_id: '',
    secteur_id: '',
    marque: '',
    modele: '',
    numero_serie: '',
    date_mise_service: '',
    date_derniere_vgp: '',
    statut: 'actif'
  });
  
  // Calculate date_prochaine_vgp automatically based on type_materiel duration
  const [vgpDuration, setVgpDuration] = useState(6);
  
  const calculatedProchaineVgp = formData.date_derniere_vgp 
    ? new Date(new Date(formData.date_derniere_vgp).setMonth(new Date(formData.date_derniere_vgp).getMonth() + vgpDuration)).toISOString().split('T')[0]
    : null;

  // Load categories CACES
  useEffect(() => {
    const loadCategoriesCACES = async () => {
      if (ctxCategoriesCACES && ctxCategoriesCACES.length > 0) {
        setCategoriesCACES(ctxCategoriesCACES);
      } else {
        try {
          const data = await api.getCategoriesCACES();
          setCategoriesCACES(data || []);
        } catch (error) {
          console.error('Error loading categories CACES:', error);
        }
      }
    };
    loadCategoriesCACES();
  }, [ctxCategoriesCACES]);

  useEffect(() => {
    loadData();
  }, []);

  // Update VGP duration when type changes
  useEffect(() => {
    if (formData.type_materiel_id) {
      const selectedType = typesMateriel.find(t => t.id === formData.type_materiel_id);
      if (selectedType) {
        setVgpDuration(selectedType.duree_vgp_mois || selectedType.duree_ct_mois || 6);
      }
    }
  }, [formData.type_materiel_id, typesMateriel]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [materielData, typesData] = await Promise.all([
        api.getMateriel(),
        api.getTypesMateriel()
      ]);
      setMateriel(materielData);
      setTypesMateriel(typesData);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadMateriel = async () => {
    try {
      const data = await api.getMateriel();
      setMateriel(data);
    } catch (error) {
      toast.error('Erreur lors du chargement du matériel');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Map VGP fields to backend CT fields for compatibility
      const dataToSend = {
        ...formData,
        date_dernier_ct: formData.date_derniere_vgp,
        site: formData.zone || formData.site
      };
      
      if (selectedMateriel) {
        await api.updateMateriel(selectedMateriel.id, dataToSend);
        toast.success('Matériel mis à jour');
      } else {
        await api.createMateriel(dataToSend);
        toast.success('Matériel ajouté');
      }
      setDialogOpen(false);
      resetForm();
      await loadMateriel();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async () => {
    if (!selectedMateriel) return;
    try {
      await api.deleteMateriel(selectedMateriel.id);
      toast.success('Matériel supprimé');
      await loadMateriel();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedMateriel(null);
    }
  };

  const openEditDialog = (item) => {
    setSelectedMateriel(item);
    setFormData({
      code: item.code || '',
      designation: item.designation || '',
      type_materiel_id: item.type_materiel_id || '',
      categorie_caces_id: item.categorie_caces_id || '',
      secteur_id: item.secteur_id || '',
      zone: item.site || item.zone || '',
      marque: item.marque || '',
      modele: item.modele || '',
      numero_serie: item.numero_serie || '',
      date_mise_service: item.date_mise_service || '',
      date_derniere_vgp: item.date_dernier_ct || item.date_derniere_vgp || '',
      statut: item.statut || 'actif'
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      code: '',
      designation: '',
      type_materiel_id: '',
      categorie_caces_id: '',
      secteur_id: '',
      zone: '',
      marque: '',
      modele: '',
      numero_serie: '',
      date_mise_service: '',
      date_derniere_vgp: '',
      statut: 'actif'
    });
    setSelectedMateriel(null);
  };

  const toggleStatut = async (item) => {
    const newStatut = item.statut === 'actif' ? 'inactif' : 'actif';
    try {
      await api.updateMateriel(item.id, { statut: newStatut });
      toast.success(`Matériel ${newStatut === 'actif' ? 'activé' : 'désactivé'}`);
      await loadMateriel();
    } catch (error) {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const zones = [...new Set(materiel.map(m => m.site || m.zone).filter(Boolean))];

  const filteredMateriel = materiel.filter(m => {
    const matchesSearch = !search ||
      m.code?.toLowerCase().includes(search.toLowerCase()) ||
      m.type?.toLowerCase().includes(search.toLowerCase()) ||
      m.marque?.toLowerCase().includes(search.toLowerCase());
    const matchesZone = zoneFilter === 'all' || (m.site || m.zone) === zoneFilter;
    return matchesSearch && matchesZone;
  });

  const getVgpStatus = (dateProchaineVgp) => {
    if (!dateProchaineVgp) return null;
    const days = Math.ceil((new Date(dateProchaineVgp) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0) return <Badge variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" />Échue</Badge>;
    if (days <= 30) return <Badge className="bg-orange-100 text-orange-800"><AlertTriangle className="h-3 w-3 mr-1" />{days}j</Badge>;
    return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />OK</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion du Matériel Roulant</h1>
          <p className="text-gray-500">Engins et équipements nécessitant une autorisation de conduite</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => navigate('/types-materiel')}
          >
            <Settings className="h-4 w-4" />
            Types & Checklists
          </Button>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="gap-2">
            <Plus className="h-4 w-4" />
            Nouveau matériel
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-100">
                <Truck className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total matériels</p>
                <p className="text-xl font-bold">{materiel.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">VGP à jour</p>
                <p className="text-xl font-bold">
                  {materiel.filter(m => {
                    if (!m.date_prochaine_ct && !m.date_fin_ct) return true;
                    return new Date(m.date_prochaine_ct || m.date_fin_ct) > new Date();
                  }).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-orange-100">
                <MapPin className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Zones</p>
                <p className="text-xl font-bold">{zones.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher par code, type, marque..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={zoneFilter} onValueChange={setZoneFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Toutes les zones" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les zones</SelectItem>
                {zones.map((zone) => (
                  <SelectItem key={zone} value={zone}>{zone}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredMateriel.length === 0 ? (
            <div className="text-center py-12">
              <Truck className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">Aucun matériel trouvé</p>
              <Button variant="link" onClick={() => { resetForm(); setDialogOpen(true); }}>
                Ajouter un matériel
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Type de matériel</TableHead>
                  <TableHead>Marque / Modèle</TableHead>
                  <TableHead>Zone</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Dernière VGP</TableHead>
                  <TableHead>Prochaine VGP</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMateriel.map((item) => (
                  <TableRow key={item.id} className={item.statut === 'inactif' ? 'opacity-60 bg-gray-50' : ''}>
                    <TableCell className="font-medium">{item.code}</TableCell>
                    <TableCell>{item.type_materiel_libelle || item.type || '-'}</TableCell>
                    <TableCell>
                      {item.marque && item.modele ? `${item.marque} ${item.modele}` : item.marque || item.modele || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        <MapPin className="h-3 w-3 mr-1" />
                        {item.site || item.zone || '-'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        className={item.statut === 'inactif' 
                          ? 'bg-gray-100 text-gray-700 cursor-pointer hover:bg-gray-200' 
                          : 'bg-green-100 text-green-700 cursor-pointer hover:bg-green-200'
                        }
                        onClick={() => toggleStatut(item)}
                      >
                        {item.statut === 'inactif' ? (
                          <><PowerOff className="h-3 w-3 mr-1" />Inactif</>
                        ) : (
                          <><Power className="h-3 w-3 mr-1" />Actif</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {item.date_dernier_ct ? (
                        <span className="text-sm">{new Date(item.date_dernier_ct).toLocaleDateString('fr-FR')}</span>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {(item.date_prochaine_ct || item.date_fin_ct) ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{new Date(item.date_prochaine_ct || item.date_fin_ct).toLocaleDateString('fr-FR')}</span>
                          {getVgpStatus(item.date_prochaine_ct || item.date_fin_ct)}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(item)} title="Modifier">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => {
                            setSelectedMateriel(item);
                            setDeleteDialogOpen(true);
                          }}
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedMateriel ? 'Modifier le matériel' : 'Nouveau matériel'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="CH-CE-001"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Désignation *</Label>
                <Input
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                  placeholder="Chariot élévateur frontal"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Type de matériel *</Label>
                <Select 
                  value={formData.type_materiel_id} 
                  onValueChange={(value) => setFormData({ ...formData, type_materiel_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {typesMateriel.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.libelle}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Zone</Label>
                <Input
                  value={formData.zone}
                  onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                  placeholder="Zone A, Entrepôt 1, etc."
                />
              </div>
              <div className="space-y-2">
                <Label>Catégorie CACES requise</Label>
                <Select 
                  value={formData.categorie_caces_id} 
                  onValueChange={(value) => setFormData({ ...formData, categorie_caces_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoriesCACES.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.code} - {cat.libelle}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Numéro de série</Label>
                <Input
                  value={formData.numero_serie}
                  onChange={(e) => setFormData({ ...formData, numero_serie: e.target.value })}
                  placeholder="SN123456789"
                />
              </div>
              <div className="space-y-2">
                <Label>Marque</Label>
                <Input
                  value={formData.marque}
                  onChange={(e) => setFormData({ ...formData, marque: e.target.value })}
                  placeholder="Fenwick"
                />
              </div>
              <div className="space-y-2">
                <Label>Modèle</Label>
                <Input
                  value={formData.modele}
                  onChange={(e) => setFormData({ ...formData, modele: e.target.value })}
                  placeholder="H25T"
                />
              </div>
              <div className="space-y-2">
                <Label>Date de mise en service</Label>
                <Input
                  type="date"
                  value={formData.date_mise_service}
                  onChange={(e) => setFormData({ ...formData, date_mise_service: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Date de la dernière VGP</Label>
                <Input
                  type="date"
                  value={formData.date_derniere_vgp}
                  onChange={(e) => setFormData({ ...formData, date_derniere_vgp: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select 
                  value={formData.statut || 'actif'} 
                  onValueChange={(value) => setFormData({ ...formData, statut: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner le statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="actif">
                      <span className="flex items-center gap-2">
                        <Power className="h-4 w-4 text-green-600" />
                        Actif
                      </span>
                    </SelectItem>
                    <SelectItem value="inactif">
                      <span className="flex items-center gap-2">
                        <PowerOff className="h-4 w-4 text-gray-500" />
                        Inactif
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Prochaine VGP (calculée automatiquement)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={calculatedProchaineVgp || ''}
                    disabled
                    className="bg-gray-100"
                  />
                  {calculatedProchaineVgp && (
                    <Badge className="bg-blue-100 text-blue-800">
                      <Calendar className="h-3 w-3 mr-1" />
                      +{vgpDuration} mois
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500">La date de la prochaine VGP est calculée automatiquement selon la durée définie pour le type de matériel</p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit">
                {selectedMateriel ? 'Mettre à jour' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer le matériel <strong>{selectedMateriel?.code}</strong> ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
